import FormGroupRenderer from '@app/components/FormGroupRenderer'
import LotSizeInput from '@app/components/LotSizeInput'
import { Area } from '@app/models/Area'
import { ISpace, Space } from '@app/models/Space'
import { bindFunctions } from '@app/utils/class'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'

interface IComponentProps {
  className?: string
  isOpen: boolean
  onConfirmClick?: (e: ISpace) => void
  onCancelClick?: () => void
  space?: ISpace
  style?: React.CSSProperties
}

interface IComponentState {
  isEditing: boolean
  space: Space
}

export default class SpaceEditorModal extends React.PureComponent<
  IComponentProps,
  IComponentState
> {
  constructor(props: any) {
    super(props)

    bindFunctions(this)

    this.state = {
      isEditing: this.props.space ? true : false,
      space:
        (this.props.space as Space) ||
        new Space(),
    }
  }

  public componentWillReceiveProps(nextProps: any) {
    if (
      nextProps.space &&
      this.props.space &&
      nextProps.space.id === this.props.space.id
    ) {
      this.setState({
        isEditing: this.props.space ? true : false,
        space:
          (this.props.space as Space) ||
          new Space(),
      })
    }
  }

  public render() {
    if (!this.state.space) {
      return null
    }

    return (
      <Modal
        size='lg'
        centered
        isOpen={this.props.isOpen}
        toggle={() => this._cancelClick(!this.props.isOpen)}
      >
        <ModalHeader toggle={() => this._cancelClick(!this.props.isOpen)}>
          {this.props.space
            ? `Edit - ${this.state.space.spaceNo}`
            : 'New Space'}
        </ModalHeader>
        <ModalBody>{this._renderSpaceFields()}</ModalBody>
        <ModalFooter>
          <Button
            color='secondary'
            outline
            onClick={() => this._cancelClick(false)}
          >
            <FontAwesomeIcon icon={['fal', 'times']} fixedWidth /> Cancel
          </Button>
          <Button color='primary' onClick={this._confirmAction}>
            <FontAwesomeIcon icon={['fal', 'save']} /> OK
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private _cancelClick(value: boolean) {
    this.setState({
      isEditing: false,
    })

    if (this.props.onCancelClick) {
      this.props.onCancelClick()
    }
  }

  private _confirmAction() {
    if (this.props.onConfirmClick) {
      const space =
        this.state.space.id === ''
          ? (this.state.space.set(
              'id',
              `space-${new Date().getTime()}`,
            ) as Space)
          : (this.state.space as Space)

      this.setState({
        isEditing: false,
        space: this.state.isEditing ? this.state.space : new Space(),
      })

      this.props.onConfirmClick(space)
    }
  }

  private _renderSpaceFields() {
    return (
      <div>
        <FormGroupRenderer
          labelText='Space No.'
          value={this.state.space.spaceNo}
          onChange={(e) => this._setSpaceNo(e)}
        />

        <FormGroupRenderer labelText='Space Available'>
          <LotSizeInput
            className='flex-grow-1 w-100'
            bsSize='sm'
            onChange={(e: Area) => this._setSpaceAvailable(e)}
            value={this.state.space.spaceAvailable}
          />
        </FormGroupRenderer>

        <FormGroupRenderer
          labelText='Minimum Divisible'
          value={this.state.space.minDivisible}
          type='number'
          onChange={(e) => this._setMinimumDivisble(e)}
        />

        <FormGroupRenderer
          labelText='Maximum Contiguous'
          value={this.state.space.maxContiguous}
          type='number'
          onChange={(e) => this._setMaximumContiguous(e)}
        />

        <FormGroupRenderer
          labelText='Space Description'
          value={this.state.space.spaceDescription}
          type='textarea'
          onChange={(e) => this._setSpaceDescription(e)}
        />

        <FormGroupRenderer
          labelText='Dock High Doors'
          value={this.state.space.noOfDockHighDoorsLoading}
          type='number'
          onChange={(e) => this._setDockHighDoors(e)}
        />

        <FormGroupRenderer
          labelText='Drive In Doors'
          value={this.state.space.noOfDriveInGradeLevelDoors}
          type='number'
          onChange={(e) => this._setDriveInDoors(e)}
        />

        <FormGroupRenderer
          labelText='Ceiling Height'
          value={this.state.space.ceilingHeight}
          type='number'
          onChange={(e) => this._setCeilingHeight(e)}
        />

        <FormGroupRenderer
          labelText='Parking Spaces'
          value={this.state.space.noOfParkingSpaces}
          type='number'
          onChange={(e) => this._setParkingSpaces(e)}
        />

        <FormGroupRenderer
          labelText='Office Square Feet'
          value={this.state.space.officeSF}
          type='number'
          onChange={(e) => this._setOfficeSquareFeet(e)}
        />
      </div>
    )
  }

  private _setCeilingHeight(value: number) {
    this.setState({
      space: this.state.space.set('ceilingHeight', value) as Space,
    })
  }

  private _setDockHighDoors(value: number) {
    this.setState({
      space: this.state.space.set('noOfDockHighDoorsLoading', value) as Space,
    })
  }

  private _setDriveInDoors(value: number) {
    this.setState({
      space: this.state.space.set('noOfDriveInGradeLevelDoors', value) as Space,
    })
  }

  private _setMaximumContiguous(value: number) {
    this.setState({
      space: this.state.space.set('maxContiguous', value) as Space,
    })
  }

  private _setMinimumDivisble(value: number) {
    this.setState({
      space: this.state.space.set('minDivisible', value) as Space,
    })
  }

  private _setOfficeSquareFeet(value: number) {
    this.setState({
      space: this.state.space.set('officeSF', value) as Space,
    })
  }

  private _setParkingSpaces(value: number) {
    this.setState({
      space: this.state.space.set('noOfParkingSpaces', value) as Space,
    })
  }

  private _setSpaceAvailable(value: Area) {
    this.setState({
      space: this.state.space.set('spaceAvailable', value) as Space,
    })
  }

  private _setSpaceDescription(value: string) {
    this.setState({
      space: this.state.space.set('spaceDescription', value) as Space,
    })
  }

  private _setSpaceNo(value: string) {
    this.setState({
      space: this.state.space.set('spaceNo', value) as Space,
    })
  }
}
